'use client';

import { useEffect, useRef } from 'react';
import { useCartStore } from '@weareconceptstudio/cart';
import { trackAddToCart } from '@/libs/dataLayer';

/**
 * Global component that tracks add_to_cart events by monitoring cart store changes
 * Works everywhere, not just where useCartWithTracking is used
 */
export default function CartTracking() {
	const prevItemsRef = useRef([]);
	const isInitialMountRef = useRef(true);
	const trackedActionsRef = useRef(new Set());

	useEffect(() => {
		// Subscribe to cart store changes
		const unsubscribe = useCartStore.subscribe((state, prevState) => {
			// Skip initial mount to avoid tracking on page load
			if (isInitialMountRef.current) {
				isInitialMountRef.current = false;
				prevItemsRef.current = [...(state.items || [])];
				return;
			}

			// Only track when items actually change
			if (state.items === prevState.items) {
				return;
			}

			const currentItems = (state.items || []).filter((item) => item && !item.is_gift);
			const prevItems = prevItemsRef.current.filter((item) => item && !item.is_gift);

			// Find newly added items or quantity increases
			currentItems.forEach((currentItem) => {
				const itemKey = `${currentItem.product?.id}-${currentItem.variantId || ''}-${currentItem.optionId || ''}`;

				// Skip if we already tracked this action recently
				if (trackedActionsRef.current.has(itemKey)) {
					return;
				}

				const prevItem = prevItems.find(
					(prev) =>
						prev.product?.id === currentItem.product?.id &&
						prev.variantId === currentItem.variantId &&
						prev.optionId === currentItem.optionId
				);

				if (!prevItem) {
					// New item added - track the full quantity
					trackAddToCart(currentItem);
					trackedActionsRef.current.add(itemKey);

					// Clear after a delay to allow retracking if needed
					setTimeout(() => {
						trackedActionsRef.current.delete(itemKey);
					}, 2000);
				} else if (prevItem.qty < currentItem.qty) {
					// Quantity increased - track only the added quantity
					const addedQty = currentItem.qty - prevItem.qty;
					const itemToTrack = {
						...currentItem,
						qty: addedQty,
					};
					trackAddToCart(itemToTrack, state.currency || 'AMD');
					trackedActionsRef.current.add(itemKey);

					// Clear after a delay
					setTimeout(() => {
						trackedActionsRef.current.delete(itemKey);
					}, 2000);
				}
			});

			prevItemsRef.current = [...(state.items || [])];
		});

		return () => {
			unsubscribe();
		};
	}, []);

	return null;
}
